import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageHeader } from "../components/PageHeader";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { fmtDateTime, titleCase } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const BURG = { background: "#8B1E1E" };

export default function Administration() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ modules: [], actions: [] });
  const [locations, setLocations] = useState([]);

  const [uOpen, setUOpen] = useState(false);
  const [uForm, setUForm] = useState({});
  const [uEdit, setUEdit] = useState(null);
  const [delUser, setDelUser] = useState(null);

  const [rOpen, setROpen] = useState(false);
  const [rForm, setRForm] = useState({ name: "", description: "", permissions: {}, is_admin: false });
  const [rEdit, setREdit] = useState(null);
  const [delRole, setDelRole] = useState(null);

  const load = () => {
    api.get("/users").then(setUsers).catch(() => {});
    api.get("/roles").then(setRoles).catch(() => {});
    api.get("/roles/meta").then(setMeta).catch(() => {});
    api.get("/locations").then(setLocations).catch(() => {});
    api.get("/audit-logs").then(setLogs).catch(() => {});
  };
  useEffect(load, []);

  const openUser = (u) => { setUEdit(u); setUForm(u ? { ...u, password: "" } : { status: "active" }); setUOpen(true); };
  const saveUser = async () => {
    try {
      if (uEdit) await api.put(`/users/${uEdit.id}`, uForm);
      else await api.post("/users", uForm);
      toast.success("Saved"); setUOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeUser = async () => { try { await api.del(`/users/${delUser}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setDelUser(null); } };

  const openRole = (r) => {
    setREdit(r);
    setRForm(r ? { ...r, permissions: { ...(r.permissions || {}) } } : { name: "", description: "", permissions: {}, is_admin: false });
    setROpen(true);
  };
  const togglePerm = (mod, act) => {
    setRForm((f) => {
      const perms = { ...f.permissions };
      const list = new Set(perms[mod] || []);
      if (list.has(act)) list.delete(act); else list.add(act);
      perms[mod] = [...list];
      return { ...f, permissions: perms };
    });
  };
  const saveRole = async () => {
    try {
      if (rEdit) await api.put(`/roles/${rEdit.id}`, rForm);
      else await api.post("/roles", rForm);
      toast.success("Role saved"); setROpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeRole = async () => { try { await api.del(`/roles/${delRole}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setDelRole(null); } };

  const userCols = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span> },
    { key: "email", label: "Email" },
    { key: "role_name", label: "Role", render: (r) => <span className="text-xs px-2 py-0.5 rounded-full border" style={{ background: "#F3ECE2", color: "#8B1E1E", borderColor: "#E6D9C9" }}>{r.role_name}</span> },
    { key: "location_name", label: "Location" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "_a", label: "", sortable: false, align: "right", render: (r) => (
        <div className="flex justify-end gap-1">
          {can("administration", "edit") && <button onClick={() => openUser(r)} className="p-1.5 rounded-lg hover:bg-[#F3ECE2]" style={{ color: "#8B1E1E" }} data-testid={`user-edit-${r.id}`}><Pencil size={15} /></button>}
          {can("administration", "delete") && <button onClick={() => setDelUser(r.id)} className="p-1.5 rounded-lg hover:bg-[#F7E5E5]" style={{ color: "#A52222" }} data-testid={`user-delete-${r.id}`}><Trash2 size={15} /></button>}
        </div>
    )},
  ];
  const roleCols = [
    { key: "name", label: "Role", render: (r) => <span className="font-medium flex items-center gap-1.5" style={{ color: "#3B2722" }}>{r.is_admin && <ShieldCheck size={14} style={{ color: "#C49A4A" }} />}{r.name}</span> },
    { key: "description", label: "Description" },
    { key: "_p", label: "Modules", render: (r) => <span className="text-xs" style={{ color: "#765A4A" }}>{r.is_admin ? "All access" : `${Object.keys(r.permissions || {}).length} modules`}</span> },
    { key: "_a", label: "", sortable: false, align: "right", render: (r) => (
        <div className="flex justify-end gap-1">
          {can("administration", "edit") && <button onClick={() => openRole(r)} className="p-1.5 rounded-lg hover:bg-[#F3ECE2]" style={{ color: "#8B1E1E" }} data-testid={`role-edit-${r.id}`}><Pencil size={15} /></button>}
          {can("administration", "delete") && <button onClick={() => setDelRole(r.id)} className="p-1.5 rounded-lg hover:bg-[#F7E5E5]" style={{ color: "#A52222" }} data-testid={`role-delete-${r.id}`}><Trash2 size={15} /></button>}
        </div>
    )},
  ];
  const logCols = [
    { key: "timestamp", label: "Time", render: (r) => fmtDateTime(r.timestamp) },
    { key: "user_name", label: "User" },
    { key: "action", label: "Action", render: (r) => <span className="text-xs px-2 py-0.5 rounded capitalize" style={{ background: "#F3ECE2", color: "#765A4A" }}>{r.action}</span> },
    { key: "module", label: "Module", render: (r) => titleCase(r.module) },
    { key: "record", label: "Record" },
  ];

  return (
    <Page>
      <PageHeader title="Administration" subtitle="Users, roles, permissions & audit trail" />
      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles & Permissions</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <DataTable columns={userCols} rows={users} searchKeys={["name", "email", "role_name"]} testIdPrefix="users"
            toolbar={can("administration", "create") && <Button onClick={() => openUser(null)} data-testid="user-add-btn" style={BURG} className="text-white"><Plus size={16} className="mr-1" /> New User</Button>} />
        </TabsContent>

        <TabsContent value="roles">
          <DataTable columns={roleCols} rows={roles} searchKeys={["name", "description"]} testIdPrefix="roles"
            toolbar={can("administration", "create") && <Button onClick={() => openRole(null)} data-testid="role-add-btn" style={BURG} className="text-white"><Plus size={16} className="mr-1" /> New Role</Button>} />
        </TabsContent>

        <TabsContent value="audit">
          <DataTable columns={logCols} rows={logs} searchKeys={["user_name", "action", "module", "record"]} testIdPrefix="audit" emptyText="No audit records" />
        </TabsContent>
      </Tabs>

      {/* user dialog */}
      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-brand">{uEdit ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={uForm.name || ""} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={uForm.email || ""} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email" disabled={!!uEdit} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={uForm.phone || ""} onChange={(e) => setUForm({ ...uForm, phone: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={uForm.role_id || ""} onValueChange={(v) => setUForm({ ...uForm, role_id: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Location</Label>
                <Select value={uForm.location_id || ""} onValueChange={(v) => setUForm({ ...uForm, location_id: v })}>
                  <SelectTrigger data-testid="user-location"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>{uEdit ? "New Password (blank = keep)" : "Password"}</Label><Input type="password" value={uForm.password || ""} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-password" /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={uForm.status || "active"} onValueChange={(v) => setUForm({ ...uForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUOpen(false)}>Cancel</Button><Button onClick={saveUser} data-testid="user-save" style={BURG} className="text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* role dialog with permission matrix */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">{rEdit ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Role Name</Label><Input value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} data-testid="role-name" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={rForm.description} onChange={(e) => setRForm({ ...rForm, description: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 mt-2 text-sm" style={{ color: "#3B2722" }}>
            <Checkbox checked={rForm.is_admin} onCheckedChange={(v) => setRForm({ ...rForm, is_admin: !!v })} data-testid="role-is-admin" />
            Super Admin (grants full access to everything)
          </label>
          {!rForm.is_admin && (
            <div className="border rounded-lg overflow-x-auto mt-2" style={{ borderColor: "#ECE4D8" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "#EFE7D6" }}>
                  <tr className="text-xs uppercase" style={{ color: "#8a6f5c" }}><th className="text-left px-3 py-2">Module</th>{meta.actions.map((a) => <th key={a} className="px-2 py-2 capitalize">{a}</th>)}</tr>
                </thead>
                <tbody>
                  {meta.modules.map((mod) => (
                    <tr key={mod} className="border-t" style={{ borderColor: "#F5EFE5" }}>
                      <td className="px-3 py-2 font-medium capitalize" style={{ color: "#3B2722" }}>{mod}</td>
                      {meta.actions.map((act) => (
                        <td key={act} className="px-2 py-2 text-center">
                          <Checkbox checked={(rForm.permissions[mod] || []).includes(act)} onCheckedChange={() => togglePerm(mod, act)} data-testid={`perm-${mod}-${act}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setROpen(false)}>Cancel</Button><Button onClick={saveRole} data-testid="role-save" style={BURG} className="text-white">Save Role</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delUser} onOpenChange={() => setDelUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={removeUser} data-testid="user-confirm-delete" style={{ background: "#A52222" }} className="text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!delRole} onOpenChange={() => setDelRole(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete role?</AlertDialogTitle><AlertDialogDescription>Users with this role will lose their permissions.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={removeRole} data-testid="role-confirm-delete" style={{ background: "#A52222" }} className="text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
